import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useDocumentEditor } from '@/stores/document/editor';
import { useHighlights } from '@/stores/highlights/highlights';
import { useEditor } from '@/stores/editor/editor';
import { generateJobSpecs, generateJobProfile } from '@/api/dfd';

export const useFirstDraft = defineStore('documentFirstDraft', () => {
  const editorStore = useEditor();
  const documentEditorStore = useDocumentEditor();
  const { updateBodyMessages } = useHighlights();

  const modalVisible = ref(false);
  const newContent = ref('');
  const isNewContentLoading = ref(false);
  const newContentLoadingStatus = ref('');

  const setModalVisible = (visible) => {
    modalVisible.value = visible;
  };

  const disabled = computed(() => editorStore.isRevisionSaving
    || editorStore.isPublishedTemplate || !editorStore.isEditable);

  const reset = () => {
    newContent.value = '';
    isNewContentLoading.value = false;
    documentEditorStore.updateFocus('');
  };

  const existingBlocks = computed(() => {
    const result = [];

    documentEditorStore.fields.filter((field) => !field.isHeaderOrFooter).forEach((field) => {
      field.sections.forEach((section) => {
        section.blocks.forEach((block) => {
          const match = (block.detected_sections || []).some(
            (detected) => ['qualifications', 'responsibilities'].includes(detected),
          );

          if (match) {
            result.push(block);
          }
        });
      });
    });

    return result;
  });

  const updateNewContent = (html) => {
    newContent.value = html;
  };

  const getGeneratedHtml = (
    qualifications,
    responsibilities,
    { responsibilities: responsibilitiesHeader, qualifications: qualificationsHeader } = {},
  ) => {
    const arrayToListItems = (array) => array.map((item) => `<li>${item}</li>`).join('');
    const responsibilitiesHeaderHtml = `<h3>${responsibilitiesHeader || 'You will:'}</h3>`;
    const qualificationsHeaderHtml = `<h3>${qualificationsHeader || 'You have:'}</h3>`;

    return `${responsibilitiesHeader === '<empty header>' ? '' : responsibilitiesHeaderHtml}<ul>
        ${arrayToListItems(responsibilities)}
      </ul>${qualificationsHeader === '<empty header>' ? '' : qualificationsHeaderHtml}<ul>
        ${arrayToListItems(qualifications)}
      </ul>
    `;
  };

  const generateWithTextStatusUpdates = async () => {
    newContentLoadingStatus.value = 'Preparing job post...';
    const secondMessageTimeout = setTimeout(() => {
      newContentLoadingStatus.value = 'Generating job post...';
    }, 2000);

    const gereratedData = await generateJobSpecs(editorStore.jobId);
    clearTimeout(secondMessageTimeout);

    newContentLoadingStatus.value = 'All done!';
    await new Promise((resolve) => { setTimeout(resolve, 500); });
    newContentLoadingStatus.value = '';

    return gereratedData;
  };

  const generateNewContent = async () => {
    try {
      documentEditorStore.updateFocus('');
      isNewContentLoading.value = true;

      const { qualifications, responsibilities, headers } = await generateWithTextStatusUpdates();
      updateNewContent(getGeneratedHtml(qualifications, responsibilities, headers));
    } catch (e) {
      setModalVisible(false);
      useAppSnackbar().addErrorSnackbar({ text: 'Failed to generate new content' });
      throw e;
    } finally {
      isNewContentLoading.value = false;
    }
  };

  const applyNewContent = () => {
    const fields = [];

    let updatedEditor = null;
    let firstField = null;

    documentEditorStore.fields.forEach((originalField) => {
      // clone the field
      const field = JSON.parse(JSON.stringify(originalField));

      // ignore header, footer and lockable fields.
      // lockable fields are not synced back to ATS and are not "content" fields
      if (!field.isHeaderOrFooter && !field.isLockable) {
        // save first field for future use
        if (!firstField) {
          firstField = field;
        }

        field.sections.forEach((section) => {
          // eslint-disable-next-line no-param-reassign
          section.blocks = section.blocks.reduce((res, block) => {
            // find qualifications and responsibilities blocks
            const match = (block.detected_sections || []).some(
              (detected) => ['qualifications', 'responsibilities'].includes(detected),
            );
            if (!match) {
              res.push(block);
            } else if (!updatedEditor) {
              // place all AI generated content
              // into the first existing qualifications or responsibilities block
              res.push({ ...block, html: newContent.value });
              // save corresponding tiptap instance for future use
              updatedEditor = documentEditorStore.tiptap[field.type];
            }
            return res;
          }, []);
        });
      }

      fields.push(field);
    });

    if (!updatedEditor && firstField) {
      // job has no qualifications or responsibilities blocks
      // place AI generated content into the first existing block
      const firstSection = firstField.sections?.[0] || {};
      const firstBlock = firstSection.blocks?.[0];
      if (firstBlock) {
        firstBlock.html = newContent.value + firstBlock.html;
        updatedEditor = documentEditorStore.tiptap[firstField.type];
      }
    }

    // remove all highlights
    updateBodyMessages({});
    // update job description editors
    documentEditorStore.updateFields(fields);

    if (updatedEditor) {
      // wait for the content change to settle and emit an event that triggers rescore
      setTimeout(() => {
        updatedEditor.emit('update', {});
      }, 100);
    }
  };

  const generateAndApplyJobProfile = async ({ ulid, useExistingSummary = false, summaryFieldId }) => {
    try {
      documentEditorStore.updateFocus('');
      isNewContentLoading.value = true;
      newContentLoadingStatus.value = 'Preparing job profile...';

      const secondMessageTimeout = setTimeout(() => {
        newContentLoadingStatus.value = 'Generating job profile...';
      }, 2000);

      const { profile = {} } = await generateJobProfile(ulid, { useExistingSummary });

      clearTimeout(secondMessageTimeout);
      newContentLoadingStatus.value = 'All done!';
      await new Promise((resolve) => { setTimeout(resolve, 500); });
      newContentLoadingStatus.value = '';

      Object.entries(profile).forEach(([fieldId, html]) => {
        if (useExistingSummary && fieldId === summaryFieldId) return;
        const editor = documentEditorStore.tiptap[fieldId];
        if (editor) {
          editor.chain()
            .setMeta('addToHistory', false)
            .setContent(html, true, { preserveWhitespace: true })
            .run();
        }
      });

      setTimeout(() => {
        Object.keys(profile).forEach((fieldId) => {
          if (useExistingSummary && fieldId === summaryFieldId) return;
          const editor = documentEditorStore.tiptap[fieldId];
          if (editor) {
            editor.emit('update', {});
          }
        });
      }, 100);
    } catch (e) {
      useAppSnackbar().addErrorSnackbar({ text: `Failed to generate job profile - ${e.message}` });
      throw e;
    } finally {
      isNewContentLoading.value = false;
    }
  };

  const generateAndApply = async () => {
    try {
      documentEditorStore.updateFocus('');
      isNewContentLoading.value = true;
      const { qualifications, responsibilities, headers } = await generateWithTextStatusUpdates();
      updateNewContent(getGeneratedHtml(qualifications, responsibilities, headers));
      applyNewContent();
    } catch (e) {
      useAppSnackbar().addErrorSnackbar({ text: `Failed to generate new content - ${e.message}` });
      throw e;
    } finally {
      isNewContentLoading.value = false;
    }
  };

  return {
    modalVisible,
    newContent,
    isNewContentLoading,
    newContentLoadingStatus,
    disabled,
    existingBlocks,
    setModalVisible,
    reset,
    generateNewContent,
    updateNewContent,
    applyNewContent,
    generateAndApply,
    generateAndApplyJobProfile,
  };
});
